/**
 * Design Tokens - Central design system for TeamTab
 * All pages should use these tokens for consistent styling
 */

export const designTokens = {
  // Background Gradients
  backgrounds: {
    page: "bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900",
    pageAlt:
      "bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 dark:from-slate-900 dark:via-purple-900/20 dark:to-slate-900",
  },

  // Glassmorphism Card Styles
  cards: {
    glass:
      "bg-white/60 dark:bg-slate-800/60 backdrop-blur-xl border-0 shadow-xl hover:shadow-2xl transition-all duration-300",
    glassHover:
      "bg-white/60 dark:bg-slate-800/60 backdrop-blur-xl border-0 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300",
    glassClickable:
      "bg-white/60 dark:bg-slate-800/60 backdrop-blur-xl border-0 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 group cursor-pointer",
    solid:
      "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-lg",
  },

  // Gradient Icon Backgrounds
  iconGradients: {
    purple:
      "bg-gradient-to-br from-purple-500 to-pink-500 shadow-lg shadow-purple-500/30",
    blue: "bg-gradient-to-br from-blue-500 to-indigo-500 shadow-lg shadow-blue-500/30",
    green:
      "bg-gradient-to-br from-green-500 to-emerald-500 shadow-lg shadow-green-500/30",
    orange:
      "bg-gradient-to-br from-orange-500 to-amber-500 shadow-lg shadow-orange-500/30",
    red: "bg-gradient-to-br from-red-500 to-rose-500 shadow-lg shadow-red-500/30",
    cyan: "bg-gradient-to-br from-cyan-500 to-blue-500 shadow-lg shadow-cyan-500/30",
    indigo:
      "bg-gradient-to-br from-indigo-500 to-purple-500 shadow-lg shadow-indigo-500/30",
  },

  // Button Gradients
  buttons: {
    primary:
      "bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200",
    success:
      "bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200",
    danger:
      "bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200",
    warning:
      "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200",
    info: "bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200",
    purple:
      "bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200",
  },

  // Text Gradients
  textGradients: {
    primary:
      "bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 dark:from-indigo-400 dark:via-purple-400 dark:to-pink-400 bg-clip-text text-transparent",
    blue: "bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent",
    green:
      "bg-gradient-to-r from-green-600 to-emerald-600 dark:from-green-400 dark:to-emerald-400 bg-clip-text text-transparent",
    orange:
      "bg-gradient-to-r from-orange-600 to-amber-600 dark:from-orange-400 dark:to-amber-400 bg-clip-text text-transparent",
  },

  // Tab Styles
  tabs: {
    container:
      "bg-white/60 dark:bg-slate-800/60 backdrop-blur-xl border-0 shadow-xl p-2",
    trigger:
      "data-[state=active]:text-white data-[state=active]:shadow-lg transition-all",
    triggerPurple:
      "data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-pink-500",
    triggerBlue:
      "data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-indigo-500",
    triggerGreen:
      "data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-emerald-500",
    triggerOrange:
      "data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-amber-500",
  },

  // Stat Card Styles
  statCards: {
    iconWrapper:
      "flex-shrink-0 rounded-xl p-3 shadow-lg group-hover:scale-110 transition-transform",
    label: "text-sm font-medium text-slate-600 dark:text-slate-400 mb-1",
    value: "text-xl sm:text-2xl font-black text-slate-900 dark:text-slate-100",
    subtitle:
      "text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1",
  },

  // Typography
  typography: {
    h1: "text-3xl sm:text-4xl font-black",
    h2: "text-2xl sm:text-3xl font-bold",
    h3: "text-xl sm:text-2xl font-semibold",
    h4: "text-lg sm:text-xl font-semibold",
    body: "text-sm sm:text-base text-slate-600 dark:text-slate-400",
    small: "text-xs text-slate-500 dark:text-slate-400",
  },

  // Spacing
  spacing: {
    page: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8",
    section: "mb-6 sm:mb-8",
    cardPadding: "p-4 sm:p-6",
    cardGap: "space-y-3 sm:space-y-4",
  },

  // Animations
  animations: {
    fadeIn: "fade-in",
    slideUp: "animate-slide-up",
    scaleIn: "animate-scale-in",
  },
};

// Helper function to combine classes
export const cn = (...classes) => {
  return classes.filter(Boolean).join(" ");
};
